import { ChevronRight, Columns3, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/components/ui/sheet';
import { cn } from '~/utils/cn';

import type { ExportFieldPreset, ExportTemplateColumn } from '../types/export.types';
import { resolveDefaultSelection } from '../utils/export-column-selection';

import { ExportColumnList } from './export-column-list';
import { ExportColumnManifestButton } from './export-column-manifest-button';

interface ExportColumnDrawerProps {
  columns: ExportTemplateColumn[];
  /** Committed selection. The drawer edits a draft and only calls back on Apply. */
  selected: string[];
  onApply: (selected: string[]) => void;
  /** The run's enriched attributes, for the "Run attributes" reset. */
  runFields: readonly string[] | undefined;
  /** The connector's standard starting columns when `runFields` is unknown — what Set Default
   *  restores then (`standardDefaultFields`). */
  fallbackFields?: readonly string[] | undefined;
  /** Run attributes with no matching connector column — surfaced, not swallowed. */
  unmatched: string[];
  presets: ExportFieldPreset[];
  /** The active template's encoding delta, for the downloadable manifest. */
  transformations: Record<string, string>;
  fileLabel: string;
  /** Render the Column list download beside the trigger. Off when the caller
   *  places `ExportColumnManifestButton` itself, e.g. at the end of a toolbar. */
  showManifestButton?: boolean;
  /** 'button' (outlined "Choose columns" + count — the default) or 'row' (a quiet "57 of 58 ›"
   *  value for a label/value setup row, e.g. the catalog builder's Layout card). */
  trigger?: 'button' | 'row' | 'none';
  /** Controlled open state — pass both to open the drawer from elsewhere (e.g. a status chip);
   *  omit for the drawer to own it. `trigger="none"` renders no trigger of its own. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Column selection as a drawer, matching how the other pages in this app do it
 * (see `ManageColumnsDrawer`): a trigger carrying the count, a searchable list
 * inside, and a footer whose Apply commits.
 *
 * Draft-then-apply rather than live editing, for the same reason the other
 * drawers do it: each change here re-reads a page and a count from the server,
 * so committing 12 ticks as one change is the difference between one request
 * and twelve. It also makes Cancel mean something.
 *
 * Nothing is locked: the run's enriched attributes are what the list starts
 * checked with, and every column — identifiers included — can be toggled from
 * there.
 */
export function ExportColumnDrawer({
  columns,
  selected,
  onApply,
  runFields,
  fallbackFields,
  unmatched,
  presets,
  transformations,
  fileLabel,
  showManifestButton = true,
  trigger = 'button',
  open: openProp,
  onOpenChange,
}: ExportColumnDrawerProps) {
  const { t } = useTranslation('exports');
  const [openState, setOpenState] = useState(false);
  const [draft, setDraft] = useState(new Set(selected));
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  // Opened from outside (controlled `open` flipping true) never runs `handleOpenChange`, so the
  // draft is re-seeded here too — React's adjust-state-during-render pattern, no effect needed.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(new Set(selected));
  }

  // What a fresh page opens on — one resolver, so Set Default and the initial
  // selection cannot disagree.
  const defaultKeys = resolveDefaultSelection({
    columns,
    batchAttributeKeys: runFields,
    fallbackKeys: fallbackFields,
  }).selected;

  /** Opening re-seeds the draft from the committed selection, so a cancelled
   *  edit cannot leak into the next open. */
  const handleOpenChange = (next: boolean) => {
    if (next) setDraft(new Set(selected));
    setOpen(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Sheet open={open} onOpenChange={handleOpenChange}>
        {trigger !== 'none' && (
          <SheetTrigger asChild>
            {trigger === 'row' ? (
              <button
                type="button"
                aria-label={t('columns.title')}
                className="text-foreground hover:text-primary flex cursor-pointer items-center gap-1 text-[13px] leading-tight font-medium"
              >
                {t('columns.count', { selected: selected.length, total: columns.length })}
                <ChevronRight aria-hidden="true" className="text-muted-foreground size-3.5" />
              </button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 cursor-pointer text-xs"
              >
                <Columns3 aria-hidden="true" className="mr-1.5 size-4" />
                {t('columns.trigger')}
                <Badge
                  variant="secondary"
                  className="pointer-events-none ml-1.5 text-[11px] font-normal"
                >
                  {t('columns.count', { selected: selected.length, total: columns.length })}
                </Badge>
              </Button>
            )}
          </SheetTrigger>
        )}

        {/* Same width and anatomy as the app's other Manage Columns drawers
            (`ManageColumnsDrawer`): icon title, grouped toggle rows, a
            Set Default + Apply footer. */}
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[480px]"
        >
          <SheetHeader className="flex-row items-start gap-3 space-y-0 border-b px-[18px] py-4 pr-12 text-left">
            <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-[10px]">
              <Columns3 aria-hidden="true" className="size-[18px]" />
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <SheetTitle className="text-foreground text-[16px] leading-snug font-semibold">
                {t('columns.title')}
              </SheetTitle>
              <SheetDescription className="text-muted-foreground text-[13px] leading-snug">
                {t('columns.draftCount', { selected: draft.size, total: columns.length })}
                {' · '}
                {runFields && runFields.length > 0
                  ? t('estimate.columns.batchHint')
                  : fallbackFields && fallbackFields.length > 0
                    ? t('estimate.columns.standardHint')
                    : t('estimate.columns.allHint')}
              </SheetDescription>
            </div>
          </SheetHeader>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <ExportColumnList
              columns={columns}
              draft={draft}
              onDraftChange={setDraft}
              runFields={runFields}
              unmatched={unmatched}
              presets={presets}
            />
          </div>

          <SheetFooter className="flex-row items-center justify-between gap-2 border-t px-[18px] py-3 sm:justify-between">
            {/* "Default" is what a fresh page opens on: the run's attributes plus
                identifiers when the run recorded them, every column otherwise. */}
            <Button
              type="button"
              variant="outline"
              className="h-8 cursor-pointer gap-1.5 px-3 text-[13px] font-medium"
              onClick={() => {
                setDraft(new Set(defaultKeys));
              }}
            >
              <RotateCcw aria-hidden="true" className="size-3.5" />
              {t('columns.setDefault')}
            </Button>
            {/* Blocked at zero columns, like the export itself: an empty table
                is not what a cleared list means. */}
            <Button
              type="button"
              className={cn(
                'h-8 px-4 text-[13px] font-medium',
                draft.size === 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              )}
              aria-disabled={draft.size === 0}
              onClick={() => {
                if (draft.size === 0) return;
                onApply(
                  columns.filter((column) => draft.has(column.key)).map((column) => column.key),
                );
                setOpen(false);
              }}
            >
              {t('columns.apply')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {showManifestButton && (
        <ExportColumnManifestButton
          columns={columns}
          selected={selected}
          transformations={transformations}
          fileLabel={fileLabel}
        />
      )}
    </div>
  );
}
